// Client-side push helper. Registers the service worker, subscribes to push,
// and uploads the subscription to the server.
import { api } from './api.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) throw new Error('service worker not supported');
  let reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  if (reg.installing) await new Promise(resolve => {
    reg.installing.addEventListener('statechange', e => { if (e.target.state === 'activated') resolve(); });
  });
  return reg;
}

export async function getCurrentSubscription() {
  if (!pushSupported()) return null;
  const reg = await ensureServiceWorker();
  return await reg.pushManager.getSubscription();
}

function bytesEqual(a, b) {
  if (!a || !b) return false;
  const av = a instanceof ArrayBuffer ? new Uint8Array(a) : new Uint8Array(a);
  const bv = b instanceof ArrayBuffer ? new Uint8Array(b) : new Uint8Array(b);
  if (av.length !== bv.length) return false;
  for (let i = 0; i < av.length; i++) if (av[i] !== bv[i]) return false;
  return true;
}

export async function subscribeToPush() {
  if (!pushSupported()) throw new Error('push not supported on this browser');
  const reg = await ensureServiceWorker();
  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(`notification permission ${permission}`);

  const { publicKey } = await api.get('/api/push/vapid-public-key');
  if (!publicKey) throw new Error('no VAPID public key from server');
  const expectedKey = urlBase64ToUint8Array(publicKey);

  // If there's an existing subscription with a different applicationServerKey
  // (e.g. server's VAPID keypair was rotated since last subscribe),
  // unsubscribe locally + on the server, then resubscribe fresh.
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    const existing = sub.options?.applicationServerKey;
    if (!existing || !bytesEqual(existing, expectedKey.buffer)) {
      try { await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint }); } catch {}
      await sub.unsubscribe();
      sub = null;
    }
  }

  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: expectedKey,
    });
  }

  const subJson = sub.toJSON();
  await api.post('/api/push/subscribe', {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
    deviceLabel: deviceLabelFromUA(),
  });

  return sub;
}

export async function unsubscribeFromPush() {
  const reg = await ensureServiceWorker();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint });
  await sub.unsubscribe();
}

export async function sendTest() {
  return api.post('/api/push/test');
}

function deviceLabelFromUA() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Web';
}

/** iOS PWA install heuristic — push only works after Add-to-Home-Screen on iOS */
export function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || navigator.standalone === true;
}

export function isIos() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}
